import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Button } from "./Button";
import { EyebrowHeading, Section } from "./Section";
import { completeContact, getRecaptchaSiteKey, submitContact } from "@/lib/contact.functions";
import { track, trackConversion, trackFunnelStep } from "@/lib/analytics";
import { useLocale } from "@/i18n/LocaleProvider";


declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}

const SCRIPT_ID = "recaptcha-v3-script";

function useRecaptcha(siteKey: string | undefined) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!siteKey || typeof window === "undefined") return;
    if (window.grecaptcha) {
      window.grecaptcha.ready(() => setReady(true));
      return;
    }
    if (document.getElementById(SCRIPT_ID)) return;
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    s.async = true;
    s.defer = true;
    s.onload = () => window.grecaptcha?.ready(() => setReady(true));
    document.head.appendChild(s);
  }, [siteKey]);
  return ready;
}

type Intent = "pulse" | "prime" | "podd" | "live" | "post" | "annat";
type Budget = "under-30k" | "30-60k" | "60-120k" | "over-120k" | "vet-inte";
type Timeline = "asap" | "1-3-man" | "3-6-man" | "utforskar";

const INTENT_OPTIONS_SV: { value: Intent; label: string }[] = [
  { value: "pulse", label: "Pulse-abonnemang" },
  { value: "prime", label: "Större produktion (Prime)" },
  { value: "podd", label: "Podcast / studiopodd" },
  { value: "live", label: "Live, webinar eller event" },
  { value: "post", label: "Endast post-produktion" },
  { value: "annat", label: "Något annat / vet inte än" },
];

const INTENT_OPTIONS_EN: { value: Intent; label: string }[] = [
  { value: "pulse", label: "Pulse subscription" },
  { value: "prime", label: "Larger production (Prime)" },
  { value: "podd", label: "Podcast / studio podcast" },
  { value: "live", label: "Live, webinar or event" },
  { value: "post", label: "Post-production only" },
  { value: "annat", label: "Something else / not sure yet" },
];

const BUDGET_OPTIONS_SV: { value: Budget; label: string }[] = [
  { value: "under-30k", label: "Under 30k" },
  { value: "30-60k", label: "30–60k" },
  { value: "60-120k", label: "60–120k" },
  { value: "over-120k", label: "120k+" },
  { value: "vet-inte", label: "Vet inte än" },
];

const BUDGET_OPTIONS_EN: { value: Budget; label: string }[] = [
  { value: "under-30k", label: "Under 30k" },
  { value: "30-60k", label: "30–60k" },
  { value: "60-120k", label: "60–120k" },
  { value: "over-120k", label: "120k+" },
  { value: "vet-inte", label: "Not sure yet" },
];

const TIMELINE_OPTIONS_SV: { value: Timeline; label: string }[] = [
  { value: "asap", label: "Så snart som möjligt" },
  { value: "1-3-man", label: "Inom 1–3 mån" },
  { value: "3-6-man", label: "Inom 3–6 mån" },
  { value: "utforskar", label: "Utforskar bara" },
];

const TIMELINE_OPTIONS_EN: { value: Timeline; label: string }[] = [
  { value: "asap", label: "As soon as possible" },
  { value: "1-3-man", label: "Within 1–3 months" },
  { value: "3-6-man", label: "Within 3–6 months" },
  { value: "utforskar", label: "Just exploring" },
];

export function ContactForm({
  location = "kontakt",
  defaultIntent = "annat",
  id,
  eyebrow,
  title,
  lede,
}: {
  location?: string;
  defaultIntent?: Intent;
  id?: string;
  eyebrow?: string;
  title?: string;
  lede?: string;
} = {}) {
  const en = useLocale() === "en";
  const fetchKey = useServerFn(getRecaptchaSiteKey);
  const submit = useServerFn(submitContact);
  const complete = useServerFn(completeContact);
  const { data: keyData } = useQuery({
    queryKey: ["recaptcha-site-key"],
    queryFn: () => fetchKey(),
    staleTime: Infinity,
  });
  const siteKey = keyData?.siteKey;
  const ready = useRecaptcha(siteKey);

  const [status, setStatus] = useState<"idle" | "sending" | "ok" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [budget, setBudget] = useState<Budget | null>(null);
  const [timeline, setTimeline] = useState<Timeline | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [leadId, setLeadId] = useState<string | null>(null);
  const [detailStatus, setDetailStatus] = useState<"idle" | "sending" | "ok" | "error">("idle");
  const formRef = useRef<HTMLFormElement>(null);
  const interactedRef = useRef(false);

  useEffect(() => {
    trackFunnelStep("contact_form_view", { location });
  }, [location]);

  function handleFirstInteraction() {
    if (interactedRef.current) return;
    interactedRef.current = true;
    trackFunnelStep("contact_form_first_interaction", { location });
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    trackFunnelStep("contact_form_submit_attempt", { location, intent: String(new FormData(e.currentTarget).get("intent") ?? defaultIntent) });
    if (!siteKey || !window.grecaptcha || !ready) {
      setError(en ? "Verification isn't ready yet. Please try again in a moment." : "Verifieringen är inte redo ännu. Försök igen om en stund.");
      track("contact_form_submit_error", { location, reason: "recaptcha_not_ready" });
      return;
    }
    setStatus("sending");
    try {
      const fd = new FormData(e.currentTarget);
      const intent = (String(fd.get("intent") ?? defaultIntent) as Intent);
      const token = await window.grecaptcha.execute(siteKey, { action: "contact" });
      const { getAttribSnapshot } = await import("@/lib/analytics/track");
      const attrib = getAttribSnapshot();
      const res = await submit({
        data: {
          name: String(fd.get("name") ?? ""),
          email: String(fd.get("email") ?? ""),
          phone: String(fd.get("phone") ?? ""),
          company: String(fd.get("company") ?? ""),
          intent,
          budget,
          timeline,
          message: String(fd.get("msg") ?? ""),
          token,
          session_id: attrib.session_id,
          visitor_id: attrib.visitor_id,
          source_path: typeof window !== "undefined" ? window.location.pathname.slice(0, 255) : null,
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
        },
      });
      if (res.ok) {
        setStatus("ok");
        setLeadId(res.id ?? null);
        formRef.current?.reset();
        trackConversion({
          conversion_type: "contact_form_submit",
          location,
          label: "Contact form",
          intent,
          has_company: Boolean(String(fd.get("company") ?? "").trim()),
        });
      } else {
        setStatus("error");
        setError(res.error);
        track("contact_form_submit_error", { location, intent, reason: "server_error", message: res.error });
      }
    } catch (err) {
      console.error(err);
      setStatus("error");
      setError(en ? "Something went wrong. Please try again." : "Något gick fel. Försök igen.");
      track("contact_form_submit_error", { location, reason: "exception" });
    }
  }

  async function onDetails() {
    if (!leadId) {
      setDetailStatus("ok");
      return;
    }
    setDetailStatus("sending");
    const root = formRef.current;
    const val = (name: string) =>
      (root?.querySelector<HTMLInputElement>(`[name="${name}"]`)?.value ?? "").trim();
    try {
      const res = await complete({
        data: {
          id: leadId,
          name: val("d_name").slice(0, 100),
          phone: val("d_phone").slice(0, 40),
          company: val("d_company").slice(0, 150),
          budget,
          timeline,
        },
      });
      if (res.ok) {
        setDetailStatus("ok");
        trackConversion({
          conversion_type: "contact_details_completed",
          location,
          label: "Contact details step 2",
          has_phone: Boolean(val("d_phone")),
        });
      } else {
        // Leadet är redan sparat — låt besökaren försöka igen med detaljerna.
        setDetailStatus("error");
      }
    } catch (err) {
      console.error(err);
      setDetailStatus("error");
    }
  }

  const intentOptions = en ? INTENT_OPTIONS_EN : INTENT_OPTIONS_SV;
  const budgetOptions = en ? BUDGET_OPTIONS_EN : BUDGET_OPTIONS_SV;
  const timelineOptions = en ? TIMELINE_OPTIONS_EN : TIMELINE_OPTIONS_SV;

  return (
    <Section id={id ?? (en ? "contact" : "kontakt")} className="scroll-mt-24 bg-surface/40">
      <div className="grid gap-12 md:grid-cols-2 md:items-center">
        <EyebrowHeading
          eyebrow={eyebrow ?? (en ? "Contact" : "Kontakt")}
          title={
            title ??
            (defaultIntent === "prime"
              ? (en
                  ? "Let's scope your Prime production together"
                  : "Låt oss skissa er Prime-produktion tillsammans")
              : defaultIntent === "podd" || defaultIntent === "live" || defaultIntent === "post"
              ? (en
                  ? "Tell us about your project — we'll come back within 24 h"
                  : "Berätta om projektet — vi återkommer inom 24 h")
              : (en
                  ? "Let's design your Pulse together in a short meeting"
                  : "Låt oss designa er Pulse under ett kort möte tillsammans"))
          }
          lede={
            lede ??
            (defaultIntent === "prime"
              ? (en
                  ? "A free 30-minute call where we walk through your idea, goals and budget — and tell you honestly if Prime is the right fit."
                  : "Kostnadsfritt 30-minutersmöte där vi går igenom idé, mål och budget — och säger ärligt om Prime är rätt väg.")
              : (en
                  ? "A free strategy call where we map your content volume and needs, recommend the right tier, and show what the monthly flow would look like."
                  : "Kostnadsfritt strategimöte där vi räknar på er contentmängd och behov, föreslår rätt nivå och visar exempel på hur månadsflödet skulle se ut."))
          }
        />

        <form data-track-form="kontakt"
          ref={formRef}
          onSubmit={onSubmit}
          onFocusCapture={handleFirstInteraction}
          onChangeCapture={handleFirstInteraction}
          className="rounded-3xl border border-white/10 bg-background p-6 md:p-8"
        >
          {status === "ok" ? (
            <div className="py-2">
              <div className="text-2xl font-semibold">{en ? "Thank you!" : "Tack!"}</div>
              <p className="mt-2 text-sm text-muted-foreground">
                {en
                  ? "We've got your email and normally reply within 24 hours, during office hours (weekdays 08:30–17:00 CET)."
                  : "Vi har din mejladress och svarar normalt inom 24 timmar, inom kontorstid (vardagar 08:30–17:00)."}
              </p>

              {detailStatus === "ok" ? (
                <p className="mt-6 rounded-2xl border border-mint/30 bg-mint/10 p-4 text-sm text-foreground">
                  {en
                    ? "Thanks — the details are with us. We'll come prepared."
                    : "Tack — detaljerna är hos oss. Vi hör av oss förberedda."}
                </p>
              ) : (
                <div className="mt-6 space-y-4 border-t border-white/10 pt-6">
                  <p className="text-sm text-foreground/90">
                    {en
                      ? "Want us to call instead? Leave your number and a few details — takes 20 seconds and gets you a sharper answer."
                      : "Vill ni hellre bli uppringda? Lämna nummer och några detaljer — tar 20 sekunder och ger er ett vassare svar."}
                  </p>
                  <Field label={en ? "Phone (optional)" : "Telefonnummer (valfritt)"} name="d_phone" type="tel" />
                  <Field label={en ? "Name" : "Namn"} name="d_name" />
                  <Field label={en ? "Company" : "Företag"} name="d_company" />
                  <ChipGroup
                    label={en ? "Approx. budget" : "Ungefärlig budget"}
                    options={budgetOptions}
                    value={budget}
                    onChange={setBudget}
                  />
                  <ChipGroup
                    label={en ? "Timeline" : "Tidsplan"}
                    options={timelineOptions}
                    value={timeline}
                    onChange={setTimeline}
                  />
                  <Button
                    type="button"
                    variant="mint"
                    arrow
                    className="w-full"
                    disabled={detailStatus === "sending"}
                    onClick={onDetails}
                  >
                    {detailStatus === "sending"
                      ? en ? "Saving…" : "Sparar…"
                      : en ? "Send the details" : "Skicka detaljerna"}
                  </Button>
                  {detailStatus === "error" && (
                    <p className="text-center text-xs text-red-400" role="alert">
                      {en
                        ? "The details didn't save. Try again — your message is already with us."
                        : "Detaljerna sparades inte. Försök igen — er förfrågan är redan hos oss."}
                    </p>
                  )}
                  <p className="text-center text-xs text-muted-foreground">
                    {en ? "Optional — your message is already with us." : "Frivilligt — er förfrågan är redan hos oss."}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Steg 1: bara e-post och ärende krävs — resten frågar vi efter i steg 2. */}
              <Field label={en ? "Email" : "E-post"} name="email" type="email" required />
              <label className="block">
                <span className="mb-1.5 block text-xs uppercase tracking-widest text-muted-foreground">
                  {en ? "What would you like to talk about?" : "Vad vill ni prata om?"}
                </span>
                <select
                  name="intent"
                  defaultValue={defaultIntent}
                  className="w-full rounded-2xl border border-white/10 bg-surface px-4 py-3 text-sm focus:border-mint focus:outline-none focus:ring-2 focus:ring-mint/40"
                >
                  {intentOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
              <Field
                label={
                  en
                    ? "Tell us briefly about your need (optional)"
                    : "Berätta kort om ert behov (valfritt)"
                }
                name="msg"
                textarea
              />

              {/* Valfria detaljer, döljs bakom en toggle */}
              {!expanded ? (
                <button
                  type="button"
                  onClick={() => {
                    setExpanded(true);
                    handleFirstInteraction();
                    track("contact_form_expand_details", { location });
                  }}
                  className="text-sm text-mint underline underline-offset-4 hover:text-mint/80"
                >
                  {en ? "+ Add details (optional)" : "+ Lägg till detaljer (valfritt)"}
                </button>
              ) : (
                <div className="space-y-4 border-t border-white/10 pt-4">
                  <Field label={en ? "Name" : "Namn"} name="name" />
                  <Field label={en ? "Phone" : "Telefonnummer"} name="phone" type="tel" />
                  <Field label={en ? "Company" : "Företag"} name="company" />
                  <ChipGroup
                    label={en ? "Approx. budget (optional)" : "Ungefärlig budget (valfritt)"}
                    options={budgetOptions}
                    value={budget}
                    onChange={setBudget}
                  />
                  <ChipGroup
                    label={en ? "Timeline (optional)" : "Tidsplan (valfritt)"}
                    options={timelineOptions}
                    value={timeline}
                    onChange={setTimeline}
                  />
                </div>
              )}
              {error && (
                <p className="text-sm text-red-400" role="alert">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                variant="mint"
                arrow
                className="w-full"
                disabled={status === "sending"}
              >
                {status === "sending"
                  ? en ? "Sending…" : "Skickar…"
                  : en ? "Send — asking is always free" : "Skicka — det kostar inget att fråga"}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                {en
                  ? "Reply within 24 h during office hours (weekdays 08:30–17:00 CET) · First call is free · No commitment"
                  : "Svar inom 24 h inom kontorstid (vardagar 08:30–17:00) · Första samtalet är kostnadsfritt · Ingen bindning"}
              </p>
              <p className="pt-2 text-center text-sm text-foreground/80">
                {en ? "Or directly: " : "Eller direkt: "}
                <a href="mailto:hey@fenny.se" className="font-medium text-mint hover:underline">
                  hey@fenny.se
                </a>
                <span className="mx-2 text-muted-foreground">•</span>
                <a href="tel:+46848004706" className="font-medium text-mint hover:underline">
                  08 480 04 706
                </a>
              </p>
              <p className="text-center text-xs text-muted-foreground">
                {en ? "Protected by reCAPTCHA. Google " : "Skyddat av reCAPTCHA. Google "}
                <a
                  href="https://policies.google.com/privacy"
                  className="underline underline-offset-2"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Privacy
                </a>{" "}
                &{" "}
                <a
                  href="https://policies.google.com/terms"
                  className="underline underline-offset-2"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Terms
                </a>
                .
              </p>
            </div>
          )}
        </form>
      </div>
    </Section>
  );
}

function ChipGroup<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T | null;
  onChange: (v: T | null) => void;
}) {
  return (
    <div className="block">
      <span className="mb-1.5 block text-xs uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const active = value === o.value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange(active ? null : o.value)}
              aria-pressed={active}
              className={
                "rounded-full border px-3.5 py-1.5 text-sm transition-colors " +
                (active
                  ? "border-mint bg-mint/15 text-mint"
                  : "border-white/10 bg-surface text-foreground/80 hover:border-mint/40 hover:text-foreground")
              }
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Field({
  label,
  name,
  type = "text",
  textarea = false,
  required = false,
}: {
  label: string;
  name: string;
  type?: string;
  textarea?: boolean;
  required?: boolean;
}) {
  const base =
    "w-full rounded-2xl border border-white/10 bg-surface px-4 py-3 text-sm placeholder:text-muted-foreground focus:border-mint focus:outline-none focus:ring-2 focus:ring-mint/40";
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs uppercase tracking-widest text-muted-foreground">
        {label}
        {required && <span className="ml-1 text-mint">*</span>}
      </span>
      {textarea ? (
        <textarea name={name} rows={4} required={required} className={base} />
      ) : (
        <input type={type} name={name} required={required} className={base} />
      )}
    </label>
  );
}
